import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, LOCALE_ID, NgModule, Optional, SkipSelf } from '@angular/core';

// Akita
import { NG_ENTITY_SERVICE_CONFIG } from '@datorama/akita-ng-entity-service';
import { AkitaNgRouterStoreModule } from '@datorama/akita-ng-router-store';

// App Initializer
import { APP_INIT_FACTORY, STORAGE_INFO_KEY } from '../app.initializer';
import { LocalStorageService } from '../project/service/localstorage.service';
import { IssueQuery } from '../project/state/issue/issue.query';
import { MetaInterfaceServiceProvider } from '../project/state/metaInterfaces/metaInterfaces.provider.service';
import { MetaInterfacesQuery } from '../project/state/metaInterfaces/metaInterfaces.query';
import { ProjectQuery } from '../project/state/project/project.query';
import { ProjectScopeQuery } from '../project/state/projectScope/projectScope.query';
import { WorkflowQuery } from '../project/state/workflow/workflow.query';

// Interceptors
import { AuthInterceptor } from '../interceptors/auth.interceptor';

// Core Services
import { AppEventsService } from '../project/service/appEvents.service';
import { ClientMessagesService } from '../project/service/clientMessages.service';

// Utilities
import { IconDefinition } from '@ant-design/icons-angular';
import * as AllIcons from '@ant-design/icons-angular/icons';
import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import { HttpLoaderFactory } from '@ep-om/app.module';
import { DATA_SOURCE } from '@ep-om/project/data-sources/datasource.abstract';
import { PlayersDataSource } from '@ep-om/project/data-sources/player.datasource';
import { QueryDataSource } from '@ep-om/project/data-sources/query.datasource';
import { TeamsDataSource } from '@ep-om/project/data-sources/teams.datasource';
import { UserDataSource } from '@ep-om/project/data-sources/user.datasource';
import {
  APPOINTMENT_REMOTE_GET,
  AppointmentRemoteGetByUsersHandler,
  ISSUE_CREATION,
  IssueCreationHandler
} from '@ep-om/project/state/action/handlers/handlers';
import { CheckboxFormlyComponent } from '@ep-om/shared/formly-wrappers/checkbox-formly/checkbox-formly.component';
import { DatePickerFormlyComponent } from '@ep-om/shared/formly-wrappers/date-picker/date-picker-formly.component';
import { FormlyAntIconSelectComponent } from '@ep-om/shared/formly-wrappers/formly-ant-icon-select/formly-ant-icon-select.component';
import { FormlyColorPickerComponent } from '@ep-om/shared/formly-wrappers/formly-color-picker/formly-color-picker.component';
import { FormlyDeactivableWrapperComponent } from '@ep-om/shared/formly-wrappers/formly-deactivable-wrapper/formly-deactivable-wrapper.component';
import { FormlyFileUploadComponent } from '@ep-om/shared/formly-wrappers/formly-file-upload/formly-file-upload.component';
import { FormlyFotoUploadComponent } from '@ep-om/shared/formly-wrappers/formly-foto-upload/formly-foto-upload.component';
import { FormlyIconSelectComponent } from '@ep-om/shared/formly-wrappers/formly-icon-select/formly-icon-select.component';
import { FormlyInterfaceVisibilityComponent } from '@ep-om/shared/formly-wrappers/formly-interface-visibility/formly-interface-visibility.component';
import { OmInputFormlyComponent } from '@ep-om/shared/formly-wrappers/formly-om-input/om-input-formly.component';
import { OmSelectFormlyComponent } from '@ep-om/shared/formly-wrappers/formly-om-select/om-select-formly.component';
import { OmTextareaFormlyComponent } from '@ep-om/shared/formly-wrappers/formly-om-textarea/om-textarea-formly.component';
import { FormlySelectAutocompleteComponent } from '@ep-om/shared/formly-wrappers/formly-select-autocomplete/formly-select-autocomplete.component';
import { FormlyUserSelectComponent } from '@ep-om/shared/formly-wrappers/formly-user-select/formly-user-select.component';
import { MultipleSelectComponent } from '@ep-om/shared/formly-wrappers/multiple-select/multiple-select.component';
import { RepeatSectionTypeComponent } from '@ep-om/shared/formly-wrappers/repeat-section/repeat-section.type';
import { SingleComponent } from '@ep-om/shared/formly-wrappers/single/single.component';
import { FormlySwitchComponent } from '@ep-om/shared/formly-wrappers/switch/formly-switch.component';
import { TimePickerFormlyComponent } from '@ep-om/shared/formly-wrappers/time-picker/time-picker-formly.component';
import { FormlyValidators as FV } from '@ep-om/shared/formly-wrappers/validators/formly.validators';
import { AppFormlyModule } from '@ep-om/shared/formly/formly.module';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyNgZorroAntdModule } from '@ngx-formly/ng-zorro-antd';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { it_IT, NZ_I18N } from 'ng-zorro-antd/i18n';
import { NZ_ICONS } from 'ng-zorro-antd/icon';
import { NgxEchartsModule } from 'ngx-echarts';
import { MarkdownModule } from 'ngx-markdown';
import { QuillModule } from 'ngx-quill';
import { environment } from '../../environments/environment';
import { currentStorageInfoKey } from '@persistence/session-cache';
import { getSaver, SAVER } from '../utils/saver.provider';

// Icons
const antDesignIcons = AllIcons as { [key: string]: IconDefinition };
const icons: IconDefinition[] = Object.keys(antDesignIcons).map((key) => antDesignIcons[key]);

@NgModule({
  providers: [
    { provide: STORAGE_INFO_KEY, useFactory: currentStorageInfoKey },
    // App Initializer
    {
      provide: APP_INITIALIZER,
      useFactory: APP_INIT_FACTORY,
      deps: [STORAGE_INFO_KEY, MetaInterfaceServiceProvider, LocalStorageService, ProjectQuery, WorkflowQuery, IssueQuery, ProjectScopeQuery, MetaInterfacesQuery],
      multi: true
    },

    // Interceptors
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },

    // Akita
    { provide: NG_ENTITY_SERVICE_CONFIG, useValue: { baseUrl: 'https://jsonplaceholder.typicode.com' } },

    // Utilities
    { provide: SAVER, useFactory: getSaver },
    { provide: 'SOCKET_IO_ADDRESS', useValue: `${environment.apiUrl}` },

    {
      provide: ISSUE_CREATION,
      useClass: IssueCreationHandler
    },
    {
      provide: APPOINTMENT_REMOTE_GET,
      useClass: AppointmentRemoteGetByUsersHandler
    },
    { provide: DATA_SOURCE, useClass: QueryDataSource, multi: true },
    { provide: DATA_SOURCE, useClass: UserDataSource, multi: true },
    { provide: DATA_SOURCE, useClass: PlayersDataSource, multi: true },
    { provide: DATA_SOURCE, useClass: TeamsDataSource, multi: true },
    { provide: LOCALE_ID, useValue: 'it' },
    { provide: NZ_I18N, useValue: it_IT },
    { provide: NZ_ICONS, useValue: icons },

    // Core Services
    AppEventsService,
    ClientMessagesService
  ],
  imports: [
    HttpClientModule,
    AkitaNgRouterStoreModule,

    // TUTTE le configurazioni forRoot devono essere qui

    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      defaultLanguage: 'en'
    }),
    QuillModule.forRoot(),
    MarkdownModule.forRoot(),
    environment.production ? [] : AkitaNgDevtools.forRoot(),
    NgxEchartsModule.forRoot({ echarts: () => import('echarts') }),
    FormlyModule.forRoot({
      extras: { lazyRender: true },
      types: [
        { name: 'user-select-formly', component: FormlyUserSelectComponent },
        { name: 'select-autocomplete', component: FormlySelectAutocompleteComponent },
        { name: 'multiple-select', component: MultipleSelectComponent },
        // { name: 'repeat', component: RepeatComponent },
        { name: 'checkbox-formly', component: CheckboxFormlyComponent },
        { name: 'switch', component: FormlySwitchComponent },
        { name: 'custom-checkbox', component: CheckboxFormlyComponent },
        { name: 'date', component: DatePickerFormlyComponent },
        { name: 'om-input', component: OmInputFormlyComponent },
        { name: 'om-textarea', component: OmTextareaFormlyComponent },
        { name: 'om-select', component: OmSelectFormlyComponent },
        { name: 'time', component: TimePickerFormlyComponent },
        { name: 'formly-icon-select', component: FormlyIconSelectComponent },
        { name: 'formly-ant-icon-select', component: FormlyAntIconSelectComponent },
        { name: 'formly-color', component: FormlyColorPickerComponent },
        { name: 'repeat-section', component: RepeatSectionTypeComponent },
        { name: 'interface-visibility', component: FormlyInterfaceVisibilityComponent },
        { name: 'file-upload', component: FormlyFileUploadComponent },
        { name: 'foto-upload', component: FormlyFotoUploadComponent }
      ],
      wrappers: [
        { name: 'single', component: SingleComponent },
        { name: 'deactivable', component: FormlyDeactivableWrapperComponent }
      ],
      validators: [
        { name: 'email-validation', validation: FV.EmailValidator },
        { name: 'domain-validation', validation: FV.DomainValidator },
        { name: 'fieldMatch', validation: FV.fieldMatchValidator },
        { name: 'workflow-states', validation: FV.workflowStatesValidator },
        { name: 'workflow-issueTypes', validation: FV.workflowIssueTypesValidator },
        { name: 'issueTypes-interfaces', validation: FV.issueTypesInterfacesValidator },
        { name: 'phone-validation', validation: FV.phoneValidation },
        { name: 'username-validation', validation: FV.UsernameValidator }
      ],
      validationMessages: [
        { name: 'email-validation', message: FV.EmailValidatorMessage },
        { name: 'domain-validation', message: FV.DomainValidatorMessage },
        { name: 'minlength', message: FV.minValidationMessage },
        { name: 'maxlength', message: FV.maxValidationMessage },
        { name: 'required', message: 'Il campo non può essere vuoto' },
        { name: 'unique-state', message: 'Lo stato già esiste...' },
        { name: 'unique-issueType', message: 'Il tipo di task già esiste...' },
        { name: 'unique-interface', message: "L'interfaccia già esiste..." },
        { name: 'phone-validation', message: 'Il numero di telefono non è valido...' },
        { name: 'username-validation', message: FV.UsernameValidatorMessage }
      ]
    }),
    AppFormlyModule,
    FormlyNgZorroAntdModule
  ]
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import it in the AppModule only');
    }
  }
}
